import React from 'react';
import { motion } from 'framer-motion';
import { MapPin, Phone, Mail, Facebook, Instagram, Linkedin } from 'lucide-react';
import { companyInfo } from '../mock';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const scrollToSection = (e, href) => {
    e.preventDefault();
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <footer className="relative bg-gradient-to-b from-[#2a2a2a] to-[#1a1a1a] text-white overflow-hidden">
      {/* Decorative top border */}
      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#FF6B35] via-[#9CAF88] to-[#FF6B35]" />

      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
          {/* Company Info */}
          <div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h3 className="text-2xl font-bold mb-2">ddt interiors</h3>
              <p className="text-lg text-[#9CAF88] mb-4">{companyInfo.nameChinese}</p>
              <p className="text-gray-400 leading-relaxed">
                頂級空間設計與品牌提案。源自極致美學、精密工藝與 100% 透明化承諾。
              </p>
            </motion.div>
          </div>

          {/* Quick Links */}
          <div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              viewport={{ once: true }}
            >
              <h4 className="text-lg font-bold mb-4 text-[#FF6B35]">快速連結</h4>
              <ul className="space-y-2">
                {[
                  { label: '首頁', href: '#home' },
                  { label: '關於我們', href: '#about' },
                  { label: '項目展示', href: '#projects' },
                  { label: '服務範疇', href: '#services' },
                  { label: '聯絡我們', href: '#contact' }
                ].map((link) => (
                  <li key={link.label}>
                    <a
                      href={link.href}
                      onClick={(e) => scrollToSection(e, link.href)}
                      className="text-gray-400 hover:text-[#FF6B35] transition-colors duration-300 inline-block"
                    >
                      {link.label}
                    </a>
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>

          {/* Services */}
          <div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <h4 className="text-lg font-bold mb-4 text-[#FF6B35]">服務範疇</h4>
              <ul className="space-y-2">
                {['辦公室設計', '診所設計', '美容空間設計', '零售店設計', '運動場館設計'].map((service) => (
                  <li key={service}>
                    <span className="text-gray-400">{service}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>

          {/* Contact Info */}
          <div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              viewport={{ once: true }}
            >
              <h4 className="text-lg font-bold mb-4 text-[#FF6B35]">聯絡資訊</h4>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <Phone className="w-5 h-5 text-[#9CAF88] flex-shrink-0 mt-1" />
                  <a
                    href={`https://wa.me/${companyInfo.whatsapp}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-400 hover:text-[#FF6B35] transition-colors duration-300"
                  >
                    +852 6991 3856
                  </a>
                </li>
                <li className="flex items-start gap-3">
                  <Mail className="w-5 h-5 text-[#9CAF88] flex-shrink-0 mt-1" />
                  <a
                    href={`mailto:ddt@ddtinteriors.com`}
                    className="text-gray-400 hover:text-[#FF6B35] transition-colors duration-300 break-all"
                  >
                    ddt@ddtinteriors.com
                  </a>
                </li>
              </ul>

              {/* Social Media */}
              <div className="flex gap-4 mt-6">
                {[
                  { Icon: Facebook, href: '#' },
                  { Icon: Instagram, href: '#' },
                  { Icon: Linkedin, href: '#' }
                ].map(({ Icon, href }, index) => (
                  <motion.a
                    key={index}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-[#FF6B35] transition-all duration-300"
                    whileHover={{ scale: 1.1, rotate: 5 }}
                    whileTap={{ scale: 0.9 }}
                  >
                    <Icon className="w-5 h-5" />
                  </motion.a>
                ))}
              </div>
            </motion.div>
          </div>
        </div>

        {/* Bottom Bar */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          viewport={{ once: true }}
          className="pt-8 border-t border-white/10"
        >
          <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-gray-400 text-sm">
            <p>
              © {currentYear} DDT INTERIORS {companyInfo.nameChinese}. All rights reserved.
            </p>
            <div className="flex gap-6">
              <a href="#" className="hover:text-[#FF6B35] transition-colors duration-300">
                私隱政策
              </a>
              <a href="#" className="hover:text-[#FF6B35] transition-colors duration-300">
                使用條款
              </a>
            </div>
          </div>
        </motion.div>
      </div>
    </footer>
  );
};

export default Footer;
